import React, { createContext, useContext } from "react";
import type { PropsWithChildren } from "react";
import { CircularProgress, Grid } from "@mui/material";
import { asArray } from "utils/asArray";
import { getUserAuth } from "client/api/UserAuthApi";
import useAsyncRequest from "../../hooks/useAsyncRequest";
import { ErrorAlert } from "../alert/ErrorAlert";
import type { RbacPolicyProps } from "./RbacPolicies";

export interface RbacContextState {
  isAuthorized: (policies: RbacPolicyProps) => boolean;
}

type RbacProviderProps = PropsWithChildren<unknown>;

const RbacContext = createContext<RbacContextState>({ isAuthorized: () => false });

export const useRbacContext = (): RbacContextState => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return useContext(RbacContext);
};

export function RbacProvider(props: RbacProviderProps) {
  const { children } = props;
  const { data: authorizedPolicyNames, loading, error } = useAsyncRequest(getUserAuth);

  // Performs a comparison between the user's policies and the required policies,
  // if there is multiple required policies the user is required to have all of them.
  const isAuthorized = (policiesToCheck: RbacPolicyProps): boolean => {
    return (
      !!policiesToCheck &&
      asArray(policiesToCheck).every((policy) => authorizedPolicyNames?.indexOf(policy) !== -1 || false)
    );
  };

  return (
    <>
      {!loading && !error ? (
        <RbacContext.Provider value={{ isAuthorized }}>{children}</RbacContext.Provider>
      ) : (
        <Grid
          container
          spacing={0}
          style={{ minHeight: "100vh" }}
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!error ? (
            <Grid sx={{ display: "flex", flexDirection: "column", alignItems: "center" }} size={3}>
              <CircularProgress />
              <p>Loading user data...</p>
            </Grid>
          ) : (
            <ErrorAlert error={error} />
          )}
        </Grid>
      )}
    </>
  );
}
